'use client';
import {config} from '@config';
import {Toaster} from '@lib/components/ui/sonner';
import {IntegrationManifestsProvider} from '@stores/integration-manifests';
import type {IState} from 'flagsmith/types';
import {SessionProvider} from 'next-auth/react';
import type {Session} from 'next-auth';
import {usePathname} from 'next/navigation';
import {NuqsAdapter} from 'nuqs/adapters/next/app';
import type {ReactNode} from 'react';
import {SWRConfig} from 'swr';

import Loading from './(auth)/loading';
import {DatadogFlagsLogger} from './_components/DatadogFlagsLogger';
import {FlagsProvider} from './_components/FlagsProvider';
import {StylesProvider} from './_components/StylesProvider';
import useAuthGuard from './_hooks/useAuthGuard';
import {useDatadog, useApplicationStartLogger} from './_hooks/useDatadog';
import {BackgroundTasksProvider} from './_stores/background-tasks/provider';
import {useBackgroundTasksNotifications} from './_stores/background-tasks/useBackgroundTasksNotifications';

const {
  nextauth: {
    pages: {signIn},
  },
} = config;

const InternalProviders = ({
  children,
  featureFlagsState,
}: {
  children?: ReactNode;
  featureFlagsState: IState;
  flagsEnvID?: string;
  session: Session | null;
  colorModeCookie?: string;
}) => {
  const pathname = usePathname();
  useAuthGuard({signIn});
  useBackgroundTasksNotifications();
  useDatadog();
  useApplicationStartLogger(pathname);

  return (
    <FlagsProvider flagsmithState={featureFlagsState}>
      <DatadogFlagsLogger>
        <>{children || <Loading />}</>
      </DatadogFlagsLogger>
    </FlagsProvider>
  );
};

export const Providers = (props: Parameters<typeof InternalProviders>[0]) => {
  return (
    <NuqsAdapter>
      <Toaster expand visibleToasts={5} />
      <StylesProvider
        colorModeCookie={props.colorModeCookie}
        newUIEnabled={Boolean(props.featureFlagsState.flags?.ui_new_ui?.enabled)}>
        <SessionProvider>
          <SWRConfig value={{revalidateOnFocus: false}}>
            <IntegrationManifestsProvider>
              <BackgroundTasksProvider>
                <InternalProviders {...props} />
              </BackgroundTasksProvider>
            </IntegrationManifestsProvider>
          </SWRConfig>
        </SessionProvider>
      </StylesProvider>
    </NuqsAdapter>
  );
};
